import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { faCheck, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { debounce } from "lodash";
import { type Attrs, MarkType, NodeRange, NodeType, Node as ProseNode } from "prosemirror-model";
import { EditorState, Plugin, Selection } from "prosemirror-state";
import { Transform } from "prosemirror-transform";
import { Decoration, DecorationSet } from "prosemirror-view";

import { ActionItemsContext, ActionItemsRecordingContext } from "~/actionItems/newActionItems/ActionItemsContext";
import AppContext from "~/common/AppContext";
import { getContext } from "~/lib/prose/plugins/context";
import { PasteBuiltInEmoji } from "~/lib/prose/plugins/emoji";
import FocusArea from "~/lib/prose/plugins/focusArea";
import InputRules from "~/lib/prose/plugins/inputRules";
import MentionProvider from "~/lib/prose/plugins/mention";
import Placeholder from "~/lib/prose/plugins/placeholder";
import TagProvider from "~/lib/prose/plugins/tag";
import Timestamp, { withTimestampAttrs } from "~/lib/prose/plugins/timestamp/timestamp";
import TypeAhead from "~/lib/prose/plugins/typeahead";
import type { EntityMap } from "~/lib/text";
import { Button } from "~/ui";

import { Extension } from "./extension";
import { getAllMarks } from "./marks";
import { CustomEmoji, HardBreak, HorizontalRule, Mention, Tag, TextNode, Unstyled } from "./nodes";
import ProseReact, { EditorConfig } from "./prose-react";
import { decorateText, getPosByTextIndex, isPosWithinBounds } from "./utils";

import styles from "./css/BlockTextEditor.module.css";

export enum ChangeAction {
	blur = "blur",
	esc = "esc",
	enter = "enter",
	confirm = "confirm",
	dismiss = "dismiss",
	debounce = "debounce",
}

const BLOCK_TEXT_EDITOR_EXIT_EVENT = "block-text-editor-exit";
const BLOCK_TEXT_EDITOR_SUBMIT_EVENT = "block-text-editor-submit";
const WHITELISTED_CLASSES = ["tag"];
const WHITELISTED_ATTRIBUTES = ["data-editor-input"];

class BlockTextEditorExtension extends Extension {
	closing = false;
	constructor(options: { deleteOut?: boolean; keepOpened?: boolean; submitOnEmptyBackspace?: boolean }) {
		super(options);
	}

	onSubmit = (doc: ProseNode) => {
		this.dispatchEvent(
			new CustomEvent(BLOCK_TEXT_EDITOR_SUBMIT_EVENT, { detail: { doc, action: ChangeAction.debounce } }),
		);
	};
	debouncedSubmit = debounce(this.onSubmit, 500);

	get name() {
		return "BlockEditor";
	}
	onExit = (doc: ProseNode, action: ChangeAction) => {
		this.debouncedSubmit.cancel();
		if (!this.options.keepOpened) {
			this.closing = true;
		}
		this.dispatchEvent(new CustomEvent(BLOCK_TEXT_EDITOR_EXIT_EVENT, { detail: { doc, action } }));
	};
	get plugins() {
		return [
			new Plugin({
				props: {
					handleKeyDown: (view, event) => {
						if (this.closing) return true;
						if (event.key === "Escape") {
							this.onExit(view.state.doc, ChangeAction.esc);
							return true;
						}
						if (event.key === "Enter") {
							if (event.shiftKey) return false; // new line, ignore
							this.onExit(view.state.doc, ChangeAction.enter);
							return true;
						}
						if (
							(event.key === "Backspace" || event.key === "Delete") &&
							view.state.doc.textContent.length === 0 &&
							this.options.submitOnEmptyBackspace
						) {
							if (this.options.deleteOut) {
								// deleting text on empty item deletes the whole item
								this.onExit(view.state.doc, ChangeAction.enter);
								return true;
							}
						}
						this.debouncedSubmit(view.state.doc);
						return false;
					},
					decorations: state => {
						const highlights = getContext(state)?.highlights as string[] | undefined;
						if (!highlights || !highlights.length) return null;
						const decos = highlights.reduce(
							(acc, str) => [...acc, ...decorateText(state.doc, str, { class: "search-highlight" })],
							[] as Decoration[],
						);
						return DecorationSet.create(state.doc, decos);
					},
					handleDOMEvents: {
						blur: (view, event) => {
							const focusedElement = event.relatedTarget as Element;
							const whiteListedElement =
								focusedElement &&
								(WHITELISTED_ATTRIBUTES.some(whiteListedAttribute =>
									focusedElement.hasAttribute(whiteListedAttribute),
								) ||
									WHITELISTED_CLASSES.some(whiteListedClass => focusedElement.className.includes(whiteListedClass)));

							if (this.closing || whiteListedElement) return true;
							this.onExit(view.state.doc, ChangeAction.blur);
							return true;
						},
					},
				},
			}),
		];
	}
}

interface BlockTextEditorProps {
	text: string;
	participants?: readonly { id: string; email: string; firstName: string; fullName: string; image: string }[];
	entityRanges: readonly { key: string; offset: number; length: number }[];
	entityMap: EntityMap;
	transforms?: ((node: ProseNode) => ProseNode)[];
	deleteOut?: boolean;
	submitOnEmptyBackspace?: boolean;
	className?: string;
	onContentChange?: (node: ProseNode) => void;
	enabled: boolean;
	onSubmit?: (prevDoc: ProseNode, doc: ProseNode, action: ChangeAction) => void;
	onUnmount?: () => void;
	// By hiding the controls the onSubmit will be called every so often (using a debounced function)
	// And on blur
	hideControls?: boolean;
	keepOpened?: boolean;
	version?: number;
	initialBlockAttrs?: Attrs;
}

export default function BlockTextEditor({
	text,
	participants,
	entityRanges,
	entityMap,
	transforms,
	submitOnEmptyBackspace = true,
	deleteOut,
	className,
	enabled,
	onContentChange,
	onSubmit,
	onUnmount,
	hideControls,
	keepOpened,
	version,
	initialBlockAttrs,
}: BlockTextEditorProps) {
	const { user } = useContext(AppContext);
	const { tags } = useContext(ActionItemsContext);
	const { onTimestampClick } = useContext(ActionItemsRecordingContext);
	const ref = useRef<ProseReact>(null);

	const extension: BlockTextEditorExtension = useMemo(
		() =>
			new BlockTextEditorExtension({
				submitOnEmptyBackspace,
				deleteOut,
				keepOpened,
			}),
		[deleteOut, keepOpened, submitOnEmptyBackspace],
	);

	const editorConfigGenerations = useRef(0);

	const editorConfig = useMemo(() => {
		editorConfigGenerations.current++;
		return new EditorConfig({
			nodes: { doc: { content: "unstyled" } },
			extensions: [
				...getAllMarks({
					forSearch: false,
					linkEditable: true,
					placeholderEditable: true,
					disableLinkToolBar: false,
					linkToolBarAllowEmbeds: false,
					linkToolBarCaptureMouseDown: true,
				}),
				new TextNode(),
				new (withTimestampAttrs(Unstyled))(),
				// We only want to enable timestamps for `BlockTextEditor` in places where they're expected.
				// At the time of writing, that's where `ActionItemsRecordingContext`, which provides a click handler for them,
				// is available.
				...(onTimestampClick ? [new Timestamp({ onTimestampClick })] : []),
				new Mention({ currentUserId: user?.id }),
				new HardBreak(),
				new HorizontalRule(),
				new Tag({
					showToolbar: enabled || !!user?.isGuest,
					clickable: enabled || !!user?.isGuest,
				}),
				new FocusArea({
					getTarget: (dom: HTMLElement) => dom.closest("[data-block-id]"),
				}),
				...(enabled
					? [
							new TypeAhead({
								providers: [new TagProvider({ viewerIsGuest: !!user?.isGuest }), new MentionProvider()],
							}),
						]
					: []),
				new InputRules(),
				new CustomEmoji(),
				new PasteBuiltInEmoji(),
				new Placeholder({ placeholderText: "Type out your action item, then hit “Enter“." }),
				extension,
			],
		});
	}, [user?.id, user?.isGuest, enabled, extension, onTimestampClick]);

	const initialDoc = useMemo(() => {
		let nodeType = editorConfig.schema.nodes.unstyled;
		let node: ProseNode = nodeType.create(initialBlockAttrs);
		if (text) {
			const textNodes: ProseNode[] = [];
			const textChunks = text.split("\n").map(e => e + "\n"); // eslint-disable-line prefer-template
			textChunks.forEach(chunk => {
				if (chunk.slice(0, -1).length) {
					textNodes.push(editorConfig.schema.text(chunk.slice(0, -1)));
				}
				textNodes.push(editorConfig.schema.nodes.hard_break.create());
			});
			const n = nodeType.createAndFill(node.attrs, textNodes.slice(0, -1));
			if (!n) {
				throw new Error("Can't construct node");
			}
			node = n;
		}
		const tr = new Transform(node);
		for (const entityRange of entityRanges) {
			const entity = entityMap[entityRange.key];
			if (!entity) {
				continue;
			}
			if (!isPosWithinBounds(tr.doc, entityRange.offset + entityRange.length)) {
				continue;
			}
			const entityType =
				editorConfig.schema.marks[entity.type.toLowerCase()] || editorConfig.schema.nodes[entity.type.toLowerCase()];
			if (entityType instanceof MarkType) {
				const mark = entityType.create({ ...entity.data, entityId: entityRange.key });
				tr.addMark(
					getPosByTextIndex(tr.doc, entityRange.offset),
					getPosByTextIndex(tr.doc, entityRange.offset + entityRange.length),
					mark,
				);
			}
			if (entityType instanceof NodeType) {
				const from = tr.doc.resolve(getPosByTextIndex(tr.doc, entityRange.offset));
				const to = tr.doc.resolve(getPosByTextIndex(tr.doc, entityRange.offset + entityRange.length));
				const ran = new NodeRange(from, to, from.depth);
				const attrs = entity.data;
				if (entity.data.mention) {
					// some old format; probably not in use anymore
					attrs["id"] = entity.data.mention.id;
					attrs["entityId"] = entityRange.key;
				}
				tr.wrap(ran, [{ type: entityType, attrs: attrs }]);
			}
		}
		// TODO: do inline styles too.
		node = tr.doc;
		const doc = editorConfig.schema.nodes.doc.createAndFill({}, node);
		if (!doc) {
			throw new Error(`invalid node ${JSON.stringify(node.toJSON())}`);
		}
		return doc;
	}, [entityMap, entityRanges, text]); // eslint-disable-line react-hooks/exhaustive-deps -- We don't want this to change if something changes

	const [editingDoc, setEditingDoc] = useState<ProseNode>(initialDoc);

	useEffect(() => {
		if (ref.current && enabled) {
			ref.current.focus();
			ref.current.editorView?.dispatch(ref.current.editorView.state.tr.setSelection(Selection.atEnd(editingDoc)));
		}
	}, [ref, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
	// We don't want to rerun the useEffect if editingDoc changes (it will when editing the action item)
	// If we do that everytime that the doc changes the cursor will go to the end, we just want to do that
	// On the first run

	// Refocus when version updates
	useEffect(() => {
		if (ref.current && enabled) {
			ref.current.focus();
		}
	}, [ref, enabled, version]);

	const controlledDoc = useMemo(() => {
		const node = initialDoc;
		if (!transforms) return node;
		return transforms.reduce((acc, trans) => trans(acc), node);
	}, [initialDoc, transforms]);

	const onProseChange = useCallback(
		(es: EditorState, didContentChange) => {
			if (!didContentChange) {
				return;
			}
			setEditingDoc(es.doc);
			onContentChange?.(es.doc);
		},
		[onContentChange],
	);

	const onExit = useCallback(
		(doc: ProseNode, action: ChangeAction) => {
			onSubmit?.(initialDoc.child(0), doc.content.child(0) || editingDoc.child(0), action);
			onUnmount?.();
		},
		[initialDoc, editingDoc, onSubmit, onUnmount],
	);

	useEffect(() => {
		const handleEvent = (event: CustomEvent<{ doc: ProseNode; action: ChangeAction; event: Event }>) => {
			onExit(event.detail.doc, event.detail.action);
		};
		extension.addEventListener(BLOCK_TEXT_EDITOR_EXIT_EVENT, handleEvent as EventListener);
		return () => {
			extension.removeEventListener(BLOCK_TEXT_EDITOR_EXIT_EVENT, handleEvent as EventListener);
		};
	}, [onExit, extension]);

	useEffect(() => {
		// Debounce edit will only be available if the controls are hidden
		if (!hideControls) {
			return;
		}
		const handleEvent = (event: CustomEvent<{ doc: ProseNode; action: ChangeAction; event: Event }>) => {
			onSubmit?.(initialDoc.child(0), editingDoc.child(0), event.detail.action);
		};
		extension.addEventListener(BLOCK_TEXT_EDITOR_SUBMIT_EVENT, handleEvent as EventListener);
		return () => {
			extension.removeEventListener(BLOCK_TEXT_EDITOR_SUBMIT_EVENT, handleEvent as EventListener);
		};
	}, [extension, hideControls, initialDoc, editingDoc, onSubmit]);

	const onConfirm = useCallback(
		(e?: React.MouseEvent) => {
			e?.preventDefault();
			e?.stopPropagation();
			if (extension.closing) return;
			onExit(editingDoc, ChangeAction.confirm);
		},
		[extension, editingDoc, onExit],
	);

	const onDismiss = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			if (extension.closing) return;
			onExit(editingDoc, ChangeAction.dismiss);
		},
		[extension, onExit, editingDoc],
	);

	return (
		<div className={cx(styles.editor, className)} data-block-text-editor>
			<ProseReact
				enableReactHost
				// We update this key to reset the contents of the editor if needed
				key={`${version ?? 0}-${editorConfigGenerations.current}`}
				className={cx(styles.prose, "prose-react")}
				ref={ref}
				initialDoc={enabled ? initialDoc : undefined}
				controlledDoc={!enabled ? controlledDoc : undefined}
				config={editorConfig}
				onChange={onProseChange}
				editable={enabled}
				context={{
					tags,
					participants,
					isBlockTextEditor: true,
				}}
			/>
			{enabled && !hideControls && (
				<div className={styles.controls}>
					<Button variant="secondary" size="normal" className={styles.controlButton} onClickCapture={onConfirm}>
						<FontAwesomeIcon icon={faCheck} fixedWidth />
					</Button>
					<Button variant="secondary" size="normal" className={styles.controlButton} onClickCapture={onDismiss}>
						<FontAwesomeIcon icon={faTimes} fixedWidth />
					</Button>
				</div>
			)}
		</div>
	);
}
